/**
 * IosInstallGuideCard — shown ONLY on iOS Safari (where the browser refuses
 * to expose `beforeinstallprompt`). Walks the user through the two taps
 * required to Add-to-Home-Screen. Shares localStorage snooze/installed flags
 * with `useInstallPrompt` so a user is never nagged twice.
 */
import React, { useEffect, useState } from "react";
import { X, Share, Plus, HomeIcon } from "lucide-react";
import { useIosInstallGuide } from "@/hooks/useIosInstallGuide";
import { trackInstallEvent } from "@/lib/pwaAnalytics";

const APPEAR_DELAY_MS = 4000;

export default function IosInstallGuideCard() {
  const { showGuide, dismiss } = useIosInstallGuide();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!showGuide) { setVisible(false); return undefined; }
    const t = setTimeout(() => {
      setVisible(true);
      trackInstallEvent("ios_guide_shown", "ios_guide");
    }, APPEAR_DELAY_MS);
    return () => clearTimeout(t);
  }, [showGuide]);

  if (!showGuide || !visible) return null;

  return (
    <div
      className="fixed left-4 right-4 bottom-4 z-[60] sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm animate-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-labelledby="ios-install-title"
      data-testid="ios-install-guide"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="rounded-2xl bg-[#0A2540] text-white shadow-2xl ring-1 ring-white/10 overflow-hidden">
        <div className="flex items-start gap-3 p-4">
          <div
            className="shrink-0 w-11 h-11 rounded-xl bg-[#D4AF37] text-[#0A2540] font-black text-lg flex items-center justify-center"
            style={{ fontFamily: "Poppins" }}
          >
            A
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3
                id="ios-install-title"
                className="text-base font-extrabold"
                style={{ fontFamily: "Poppins" }}
              >
                Install Advoset on iPhone
              </h3>
              <button
                onClick={dismiss}
                data-testid="ios-install-close"
                aria-label="Dismiss"
                className="text-white/50 hover:text-white/90 -mr-1 -mt-1 p-1"
              >
                <X size={16} />
              </button>
            </div>
            <p className="mt-1 text-[12px] text-white/70">
              Two taps to add Advoset to your Home Screen — no App Store needed.
            </p>

            <ol className="mt-3 space-y-2 text-[12.5px] text-white/90">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-white/15 text-[10px] font-bold flex items-center justify-center">1</span>
                <span className="flex-1">
                  Tap the <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-white/15 mx-0.5 align-middle"><Share size={12} className="text-[#D4AF37]" /></span> Share icon in the Safari toolbar.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-white/15 text-[10px] font-bold flex items-center justify-center">2</span>
                <span className="flex-1">
                  Choose <strong className="text-white">Add to Home Screen</strong>
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-white/15 mx-1 align-middle"><Plus size={12} className="text-[#D4AF37]" /></span>
                  , then tap <strong className="text-white">Add</strong>.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-white/15 text-[10px] font-bold flex items-center justify-center">3</span>
                <span className="flex-1">
                  Launch Advoset from your <span className="inline-flex items-center gap-1"><HomeIcon size={11} className="text-[#D4AF37]" /> Home Screen</span>.
                </span>
              </li>
            </ol>

            <div className="mt-3 flex gap-2">
              <button
                onClick={dismiss}
                data-testid="ios-install-later"
                className="w-full rounded-xl border border-white/15 hover:border-white/30 text-white/85 text-sm font-bold py-2.5 transition"
              >
                Got it, maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
